// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Timestamp} from '@wandb/weave/components/Timestamp';
import deepEqual from 'fast-deep-equal';
import produce from 'immer';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation} from 'react-router';
import {toast} from 'react-toastify';

import {
  TemplateVariable,
  useCreateDefaultResourceConfigMutation,
  useOrgAdminsQuery,
  useUpdateDefaultResourceConfigMutation,
  useUpdateRunQueueMutation,
} from '../../../generated/graphql';
import NoAccessPage from '../../../pages/NoAccessPage';
import {useViewer} from '../../../state/viewer/hooks';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {CodeEditor} from '../CodeEditor';
import {
  FormStateFromGqlVariable,
  JsonFromFormState,
  JsonToYamlWithHandlebars,
  ParseYamlWithHandlebarsToJson,
  TemplateVariableInput,
  VariableFormState,
} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import EmptyTemplateVariables from '../DefaultResourceConfig/EmptyTemplateVariables';
import {DefineResourceTemplateVariablesForm} from '../DefaultResourceConfig/TemplateVariableForm';
import {usePublicQueueAccess} from '../hooks';
import {Queue} from '../Queues/queueUtil';
import {WANDB_ENTITY_NAME} from '../utils';
import * as S from './TabConfig.styles';
import {isPublicQueue, viewerCanSeePublicQueueAdminTabs} from './tabUtil';

type TabConfigProps = {
  queue: Queue;
  refetchQueue?: () => void;
};

type LocationState = {
  validationErrors?: string[];
};

const convertTemplateVariablesToMap = (
  templateVariables: TemplateVariable[]
) => {
  const variablesMap: Record<string, any> = {};
  templateVariables.forEach(variable => {
    variablesMap[variable.name] = {
      description: variable.description,
      schema: JSON.parse(variable.schema),
    };
  });
  return variablesMap;
};

const MAIN_SERVICE_NAME = 'Main';

const addProjectScopeToConfigIfPublic = (config: any, queue: Queue) => {
  if (isPublicQueue(queue)) {
    return {
      ...config,
      entityName: queue.entityName,
      projectName: queue.projectName,
    };
  }
  return config;
};

export const TabConfig = ({queue, refetchQueue}: TabConfigProps) => {
  const viewer = useViewer();
  const location = useLocation<LocationState>();
  const [updateDefaultResourceConfig] =
    useUpdateDefaultResourceConfigMutation();
  const [createDefaultResourceConfig] =
    useCreateDefaultResourceConfigMutation();
  const [updateRunQueue] = useUpdateRunQueueMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [displayMode, setDisplayMode] = useState('yaml');
  const buttonColorYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const buttonColorJson = displayMode === 'json' ? 'secondary' : 'ghost';

  // Get all available services from resource_args
  const availableServices = useMemo(() => {
    const resourceArgs =
      queue.defaultResourceConfig?.config.resource_args ?? {};
    const services = Object.keys(resourceArgs);
    return services.length > 0
      ? services
      : [queue.resourceType || 'kubernetes'];
  }, [queue.defaultResourceConfig?.config, queue.resourceType]);

  const [selectedService, setSelectedService] = useState<string>(
    queue.resourceType || availableServices[0] || 'kubernetes'
  );

  // Get config for the currently selected service
  const currentServiceConfig = useMemo(() => {
    const resourceArgs =
      queue.defaultResourceConfig?.config.resource_args ?? {};
    const serviceConfig = resourceArgs[selectedService] ?? {};
    return JsonToYamlWithHandlebars(serviceConfig);
  }, [queue.defaultResourceConfig?.config, selectedService]);

  const [initialConfigValue, setInitialConfigValue] =
    useState(currentServiceConfig);
  const [configValue, setConfigValue] = useState(currentServiceConfig);
  const [isInvalidConfig, setIsInvalidConfig] = useState(false);

  const [validationErrors, setValidationErrors] = useState<string[]>(
    location?.state?.validationErrors ?? []
  );

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();

  const {data: wandbOrgData, loading: isWandbOrgLoading} = useOrgAdminsQuery({
    variables: {orgName: WANDB_ENTITY_NAME},
    skip:
      !isPublicQueue(queue) || isFeatureFlagLoading || !hasPublicQueueAccess,
  });

  const isViewerWandbOrgAdmin = useMemo(() => {
    if (isWandbOrgLoading || !wandbOrgData?.organization?.members) {
      return false;
    }

    return wandbOrgData.organization.members.some(
      member => member.user?.username === viewer?.username && member.admin
    );
  }, [
    wandbOrgData?.organization?.members,
    viewer?.username,
    isWandbOrgLoading,
  ]);

  const canInteractWithPublicQueue =
    isPublicQueue(queue) &&
    !isFeatureFlagLoading &&
    hasPublicQueueAccess &&
    isViewerWandbOrgAdmin;

  useEffect(() => {
    const resourceArgs =
      queue.defaultResourceConfig?.config.resource_args ?? {};
    const serviceConfig = resourceArgs[selectedService] ?? {};
    const configStr = JsonToYamlWithHandlebars(serviceConfig);

    setInitialConfigValue(configStr);
    setConfigValue(configStr);
    setDisplayMode('yaml');
    setIsInvalidConfig(false);
    setValidationErrors([]);
  }, [queue.defaultResourceConfig?.config, selectedService]);

  const initialVariableStates =
    queue.defaultResourceConfig?.templateVariables?.map(
      (variable: TemplateVariable) => FormStateFromGqlVariable(variable)
    );

  const [variables, setVariables] = useState<{
    [key: string]: VariableFormState;
  }>(
    initialVariableStates
      ? Object.fromEntries(initialVariableStates.map(v => [v.name, v]))
      : {}
  );

  // used to check update button
  const [initialTemplateVariableValue, setInitialTemplateVariableValue] =
    useState(variables);
  const [templateVariableValue, setTemplateVariableValue] = useState(variables);
  const updateVariables = useCallback(
    (name: string, state: VariableFormState) => {
      setVariables(prevVariables => {
        const updateVars = {...prevVariables, [name]: state};
        setTemplateVariableValue(updateVars);
        return updateVars;
      });
    },
    [setVariables, setTemplateVariableValue]
  );

  const addNewService = async () => {
    const serviceName = prompt('Enter service name:');
    if (!serviceName || serviceName.trim() === '') {
      return;
    }

    if (availableServices.includes(serviceName)) {
      toast.error('Service with this name already exists');
      return;
    }

    const {resourceType: resource, defaultResourceConfig} = queue;

    if (!defaultResourceConfig?.id) {
      toast.error('Cannot add service: queue configuration not found');
      return;
    }

    try {
      // Build the complete resource_args with the new service
      const existingResourceArgs =
        queue.defaultResourceConfig?.config.resource_args ?? {};
      const updatedResourceArgs = {
        ...existingResourceArgs,
        [serviceName]: {}, // Empty config for new service
      };

      const completeConfig = {
        resource_args: updatedResourceArgs,
      };

      // Get current variables for saving
      const variablesList: {[key: string]: TemplateVariableInput} = {};
      for (const [key, variableValue] of Object.entries(variables)) {
        const json = JsonFromFormState(key, variableValue);
        if (json != null) {
          variablesList[key] = json;
        }
      }

      const updateResp = await updateDefaultResourceConfig({
        variables: addProjectScopeToConfigIfPublic(
          {
            defaultResourceConfigID: defaultResourceConfig.id,
            resource,
            config: JSON.stringify(completeConfig),
            templateVariables: JSON.stringify(variablesList),
          },
          queue
        ),
        context: propagateErrorsContext(),
      });

      if (updateResp.errors != null) {
        throw Error(updateResp.errors.join(', '));
      }

      // Switch to the new service and set empty config
      setSelectedService(serviceName as any);
      setConfigValue('{}');
      setInitialConfigValue('{}');

      // Refresh queue data to update UI
      if (refetchQueue) {
        refetchQueue();
      }

      toast.success(`Service "${serviceName}" created successfully`);
    } catch (e) {
      const err = extractErrorMessageFromApolloError(e) ?? `${e}`;
      toast.error(`Error creating service: ${err}`);
    }
  };

  const removeService = async () => {
    if (selectedService === queue.resourceType) {
      toast.error('Cannot remove the main service');
      return;
    }

    if (!availableServices.includes(selectedService)) {
      toast.error('Service not found');
      return;
    }

    const {resourceType: resource, defaultResourceConfig} = queue;

    if (!defaultResourceConfig?.id) {
      toast.error('Cannot remove service: queue configuration not found');
      return;
    }

    const serviceToRemove = selectedService;
    const confirmRemove = confirm(
      `Are you sure you want to remove service "${serviceToRemove}"?`
    );
    if (!confirmRemove) {
      return;
    }

    try {
      // Build the complete resource_args without the removed service
      const existingResourceArgs =
        queue.defaultResourceConfig?.config.resource_args ?? {};
      const updatedResourceArgs = {...existingResourceArgs};
      delete updatedResourceArgs[serviceToRemove];

      const completeConfig = {
        resource_args: updatedResourceArgs,
      };

      // Get current variables for saving
      const variablesList: {[key: string]: TemplateVariableInput} = {};
      for (const [key, variableValue] of Object.entries(variables)) {
        const json = JsonFromFormState(key, variableValue);
        if (json != null) {
          variablesList[key] = json;
        }
      }

      const updateResp = await updateDefaultResourceConfig({
        variables: addProjectScopeToConfigIfPublic(
          {
            defaultResourceConfigID: defaultResourceConfig.id,
            resource,
            config: JSON.stringify(completeConfig),
            templateVariables: JSON.stringify(variablesList),
          },
          queue
        ),
        context: propagateErrorsContext(),
      });

      if (updateResp.errors != null) {
        throw Error(updateResp.errors.join(', '));
      }

      // Switch to main service
      const mainService = queue.resourceType || availableServices[0];
      setSelectedService(mainService);

      // Update the config display for the main service
      const mainServiceConfig = updatedResourceArgs[mainService] ?? {};
      const mainConfigStr = JsonToYamlWithHandlebars(mainServiceConfig);
      setConfigValue(mainConfigStr);
      setInitialConfigValue(mainConfigStr);

      // Refresh queue data to update UI
      if (refetchQueue) {
        refetchQueue();
      }

      toast.success(`Service "${serviceToRemove}" removed successfully`);
    } catch (e) {
      const err = extractErrorMessageFromApolloError(e) ?? `${e}`;
      toast.error(`Error removing service: ${err}`);
    }
  };

  const setEditorType = (type: string) => {
    if (displayMode === type) {
      return;
    }
    // Convert the blob from previous format to current format
    if (type === 'json') {
      try {
        const parsed = ParseYamlWithHandlebarsToJson(configValue);
        setConfigValue(JSON.stringify(parsed, null, 2));
        setDisplayMode('json');
      } catch (e) {
        toast.error('Invalid YAML');
        return;
      }
    } else if (type === 'yaml') {
      try {
        const parsed = JSON.parse(configValue);
        const strYaml = JsonToYamlWithHandlebars(parsed);
        setConfigValue(strYaml);
        setDisplayMode('yaml');
      } catch (e) {
        toast.error('Invalid JSON');
        return;
      }
    }
  };

  const onConfigChange = (newValue: string) => {
    setConfigValue(newValue);
    setIsInvalidConfig(false);
  };

  // TODO: Also check for remaining <REQUIRED>
  const validateConfig = (contents: string) => {
    try {
      if (displayMode === 'yaml') {
        ParseYamlWithHandlebarsToJson(configValue);
        return true;
      } else {
        JSON.parse(contents);
        return true;
      }
    } catch (e) {
      setIsInvalidConfig(true);
      return false;
    }
  };

  const onUpdateQueueConfig = async () => {
    setIsSubmitting(true);
    if (!validateConfig(configValue)) {
      setIsSubmitting(false);
      return;
    }

    const {resourceType: resource, defaultResourceConfig} = queue;

    // Parse the current service config
    const currentServiceConfigParsed =
      displayMode === 'yaml'
        ? ParseYamlWithHandlebarsToJson(configValue)
        : JSON.parse(configValue);

    // Build the complete resource_args with all services
    const existingResourceArgs =
      queue.defaultResourceConfig?.config.resource_args ?? {};
    const updatedResourceArgs = {
      ...existingResourceArgs,
      [selectedService]: currentServiceConfigParsed,
    };

    const completeConfig = {
      resource_args: updatedResourceArgs,
    };

    const variablesList: {[key: string]: TemplateVariableInput} = {};
    try {
      for (const [key, variableValue] of Object.entries(variables)) {
        const json = JsonFromFormState(key, variableValue);
        if (json != null) {
          variablesList[key] = json;
        } else {
          setIsSubmitting(false);
          return;
        }
      }
    } catch (e) {
      toast.error('Error parsing variables: ' + e);
      setIsSubmitting(false);
      return;
    }

    if (defaultResourceConfig?.id) {
      try {
        const updateResp = await updateDefaultResourceConfig({
          variables: addProjectScopeToConfigIfPublic(
            {
              defaultResourceConfigID: defaultResourceConfig.id,
              resource,
              config: JSON.stringify(completeConfig),
              templateVariables: JSON.stringify(variablesList),
            },
            queue
          ),
          context: propagateErrorsContext(),
        });
        if (updateResp.errors != null) {
          throw Error(updateResp.errors.join(', '));
        }
        const errs =
          updateResp.data?.updateDefaultResourceConfig
            ?.schemaValidationErrors ?? [];
        setValidationErrors(errs);
        setInitialConfigValue(configValue);
        setInitialTemplateVariableValue(templateVariableValue);
        toast.success('The queue config was successfully updated.');
      } catch (e) {
        const err = extractErrorMessageFromApolloError(e) ?? `${e}`;
        toast.error(`Error in queue configuration: ${err}`);
        setIsSubmitting(false);
        return;
      }
    } else {
      // This was a legacy queue that did not have a DRC associated with it.
      // We need to create a new DRC and associate it with the queue.
      try {
        const createDRCResp = await createDefaultResourceConfig({
          variables: {
            entityName: queue.entityName,
            resource,
            config: JSON.stringify(completeConfig),
            templateVariables: null,
          },
          context: propagateErrorsContext(),
        });

        if (createDRCResp.errors != null) {
          toast.error(`Error updating queue config: ${createDRCResp.errors}`);
          setIsSubmitting(false);
          return;
        }
        const drcID =
          createDRCResp.data?.createDefaultResourceConfig
            ?.defaultResourceConfigID;
        if (drcID == null) {
          // This should never happen, but just in case...
          toast.error(
            'Error updating queue config: unexpected response from server.  Please contact support@wandb.com'
          );
          setIsSubmitting(false);
          return;
        }

        const updateQueueResp = await updateRunQueue({
          variables: {
            queueID: queue.id,
            defaultResourceConfigID: drcID,
          },
          context: propagateErrorsContext(),
        });
        if (updateQueueResp.errors != null) {
          toast.error(`Error updating queue config: ${updateQueueResp.errors}`);
          setIsSubmitting(false);
          return;
        }

        setInitialConfigValue(configValue);
        setInitialTemplateVariableValue(templateVariableValue);
        toast.success('The queue config was successfully updated.');
      } catch (err) {
        toast.error(`Error updating queue config: ${err}`);
        setIsSubmitting(false);
        return;
      }
    }

    setIsSubmitting(false);
  };

  const onResetConfig = () => {
    onConfigChange(initialConfigValue);
  };

  const handleConfigVariables = () => {
    const newVariables = produce(variables, draft => {
      // Get all instances of '{{variable}}' from ALL service configs
      const regex = /(?<=\{\{)(.*?)(?=\}\})/g;
      const allMatches = new Set<string>();

      // Parse all service configs to find all variables
      const resourceArgs =
        queue.defaultResourceConfig?.config.resource_args ?? {};
      Object.values(resourceArgs).forEach(serviceConfig => {
        const serviceConfigStr = JSON.stringify(serviceConfig);
        const matches = serviceConfigStr.match(regex);
        if (matches) {
          matches.forEach(match => allMatches.add(match.trim()));
        }
      });

      // Also parse the current config value in case it has unsaved changes
      const currentMatches = configValue.match(regex);
      if (currentMatches) {
        currentMatches.forEach(match => allMatches.add(match.trim()));
      }

      // Add a blank entry for each new variable found across all services
      allMatches.forEach(name => {
        if (draft[name] === undefined) {
          draft[name] = {
            name,
          };
        }
      });

      // Drop all keys that are not in any service configs
      Object.keys(draft).forEach(key => {
        if (!allMatches.has(key)) {
          delete draft[key];
        }
      });
    });
    setVariables(newVariables);
  };

  const isNotChanged = useMemo(
    () =>
      configValue === initialConfigValue &&
      deepEqual(templateVariableValue, initialTemplateVariableValue),
    [
      configValue,
      initialConfigValue,
      templateVariableValue,
      initialTemplateVariableValue,
    ]
  );

  const configName =
    selectedService === queue.resourceType
      ? MAIN_SERVICE_NAME
      : selectedService;

  const allServices = useMemo(() => {
    const mainService = {
      id: queue.resourceType || 'kubernetes',
      name: MAIN_SERVICE_NAME,
    };
    const additionalServices = availableServices
      .filter(service => service !== queue.resourceType)
      .map(service => ({id: service, name: service}));

    return [mainService, ...additionalServices];
  }, [availableServices, queue.resourceType]);

  if (isPublicQueue(queue) && !viewerCanSeePublicQueueAdminTabs(viewer)) {
    return <NoAccessPage contentKey={'loggedIn'} />;
  }

  return (
    <>
      <S.MetadataTitle>Queue metadata</S.MetadataTitle>
      <S.MetadataTable>
        <S.Tr>
          <S.Property>Creation date</S.Property>
          <S.ContentPanel>
            <Timestamp value={queue.createdAt} />
          </S.ContentPanel>
        </S.Tr>
      </S.MetadataTable>

      {canInteractWithPublicQueue ? (
        <>
          <S.Section>
            <S.SectionTitle>Service Configurations</S.SectionTitle>
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <Button variant="secondary" size="small" onClick={addNewService}>
                Add Service
              </Button>
              <Button variant="ghost" size="small" onClick={removeService}>
                Remove Service
              </Button>
            </div>
          </S.Section>

          <S.ServiceConfigPanel>
            <S.ServiceTabs>
              {allServices.map((service: any) => (
                <S.ServiceTab
                  key={service.id}
                  active={selectedService === service.id}
                  onClick={() => setSelectedService(service.id)}>
                  {service.name}
                </S.ServiceTab>
              ))}
            </S.ServiceTabs>

            <S.Section>
              <S.SectionTitle>{configName} config</S.SectionTitle>
              <div>
                <Button
                  variant={buttonColorYaml}
                  onClick={() => setEditorType('yaml')}>
                  YAML
                </Button>
                <Button
                  variant={buttonColorJson}
                  onClick={() => setEditorType('json')}>
                  JSON
                </Button>
              </div>
            </S.Section>
            <CodeEditor
              value={configValue}
              language={displayMode}
              onChange={onConfigChange}
              hasWarning={validationErrors.length > 0}
            />
            {validationErrors.map((err, i) => {
              return <S.WarningMessage key={i}>{err}</S.WarningMessage>;
            })}
          </S.ServiceConfigPanel>
        </>
      ) : (
        <>
          <S.Section>
            <S.SectionTitle>Queue config</S.SectionTitle>
            <div>
              <Button
                variant={buttonColorYaml}
                onClick={() => setEditorType('yaml')}>
                YAML
              </Button>
              <Button
                variant={buttonColorJson}
                onClick={() => setEditorType('json')}>
                JSON
              </Button>
            </div>
          </S.Section>
          <CodeEditor
            value={configValue}
            language={displayMode}
            onChange={onConfigChange}
            hasWarning={validationErrors.length > 0}
          />
          {validationErrors.map((err, i) => {
            return <S.WarningMessage key={i}>{err}</S.WarningMessage>;
          })}
        </>
      )}

      <div>
        <S.TemplateVariablesHeaderContainer>
          <S.TemplateVariableHeaderText>
            Launch-time overrides{' '}
          </S.TemplateVariableHeaderText>
          <S.ParseConfigButton
            size="small"
            variant="secondary"
            onClick={handleConfigVariables}>
            Parse configuration
          </S.ParseConfigButton>
        </S.TemplateVariablesHeaderContainer>
        <S.TemplateVariableFormWrapper>
          {Object.keys(variables).length > 0 ? (
            <DefineResourceTemplateVariablesForm
              initialState={variables}
              rawConfigStr={configValue}
              onUpdateVariable={updateVariables}
            />
          ) : (
            <EmptyTemplateVariables />
          )}
        </S.TemplateVariableFormWrapper>
      </div>
      <S.ConfigUpdatePanel>
        <Button
          variant="primary"
          size="large"
          onClick={onUpdateQueueConfig}
          disabled={isSubmitting || isNotChanged}>
          Save config
        </Button>
        <Button
          variant="secondary"
          size="large"
          onClick={onResetConfig}
          disabled={isSubmitting || isNotChanged}>
          Cancel
        </Button>
        {isInvalidConfig ? (
          <S.InvalidConfig>
            Config must be valid {displayMode.toUpperCase()}
          </S.InvalidConfig>
        ) : (
          <S.OnlyFutureNotice>
            Config changes will only apply to future queued items, not existing
            ones.
          </S.OnlyFutureNotice>
        )}
      </S.ConfigUpdatePanel>
    </>
  );
};
